import React, { useCallback, useState } from 'react';
import { Box } from '@dropbox/dig-foundations';
import { SignerDeclineModal } from 'signer-app/signer-decline-modal/signer-decline-modal';
import '@dropbox/dpc-ia/index.css';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { redirectTo } from 'signer-app/utils/redirect';
import { useHistory } from 'react-router-dom';
import { ReassignModal } from 'signer-app/pages/reassign-complete/reassign-modal';
import { TestModeModal } from 'signer-app/signer-experience/test-mode-modal';
import { SignerEIdModal } from 'signer-app/signer-eid-modal/signer-eid-modal';
import ReassignBanner from 'signer-app/signer-experience/reassign-banner';
import { useLocation } from 'react-router';
import { ToolbarMenu } from 'signer-app/signer-experience/signer-toolbar/toolbar-menu';
import { ToolbarContent } from 'signer-app/signer-experience/signer-toolbar/toolbar-content';

export function SignerToolbar({
  showNextButton = true,
}: {
  showNextButton?: boolean;
}) {
  const { step, signatureRequestId } = useSignerAppParams();
  const [modals, setModals] = useState({
    decline: false,
    reassign: false,
  });

  const {
    allowReassign,
    accessCode,
    embeddedParentUrl,
    transmissionGuid,
    signatureRequest,
    isMobileReady,
    hideFormViewToggle,
  } = useSignerContext();
  const [showTestMode, setShowTestMode] = useState(!!signatureRequest.testMode);
  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const modalToggle = useCallback(
    (modalName: keyof typeof modals) => () => {
      setModals({
        ...modals,
        [modalName]: !modals[modalName],
      });
    },
    [modals, setModals],
  );
  const toggleDecline = modalToggle('decline');

  return (
    <Box data-testid="signer-toolbar">
      <ToolbarContent
        showNextButton={showNextButton}
        toolbarMenu={
          <ToolbarMenu
            isLarge={step === 'preview' && isFormViewAvailable}
            onReassign={() => {
              setModals({ reassign: true, decline: false });
            }}
            onDecline={toggleDecline}
          />
        }
      />
      {allowReassign && <ReassignBanner setModals={setModals} />}
      <ReassignModal
        tsmGuid={transmissionGuid}
        signatureRequestId={signatureRequestId}
        accessCode={accessCode ?? undefined}
        isOpen={modals.reassign}
        closeModal={() => {
          setModals((prev) => ({ ...prev, reassign: false }));
        }}
        embeddedParentUrl={embeddedParentUrl}
      />
      <TestModeModal
        showTestMode={showTestMode}
        setShowTestMode={setShowTestMode}
      />
      {modals.decline && (
        <SignerDeclineModal
          isEmbedded={!!embeddedParentUrl}
          transmissionGuid={transmissionGuid}
          onRequestClose={toggleDecline}
          onDeclineSuccess={() => {
            if (embeddedParentUrl) {
              // in-app navigation
              history.push(`/sign/${signatureRequestId}/declineComplete`);
            } else {
              // full page navigation
              redirectTo('/info/externalNothingToSign');
            }
          }}
        />
      )}
      <SignerEIdModal
        isOpen={searchParams.has('qes')}
        onRequestClose={() => history.goBack()}
      />
    </Box>
  );
}
